import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import FallbackBanner from '../../assets/banner-1.svg';

const pickLogoImage = (merchant) => (
  merchant?.logoImage?.trim()
  || merchant?.coverImage?.trim()
  || FallbackBanner
);

const FearuredMerchants = ({ merchants = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const [pulseLeft, setPulseLeft] = useState(false);
  const [pulseRight, setPulseRight] = useState(false);

  const featuredMerchants = merchants.filter(m => m.featured);

  if (!featuredMerchants.length) {
    return (
      <section className="pt-6 px-2 lg:px-5">
        <h2 className="mb-3 text-xl font-semibold text-[#000000DE]">
          {t('Featured Merchants')}
        </h2>
        <div className="text-center py-6">{t('No featured merchants available')}</div>
      </section>
    );
  }

  const handleScroll = (direction) => {
    if (!swiperRef.current) { return; }

    const swiper = swiperRef.current;
    if (direction === 'left') {
      swiper.slidePrev();
      setPulseLeft(true);
      setTimeout(() => setPulseLeft(false), 300);
    } else {
      swiper.slideNext();
      setPulseRight(true);
      setTimeout(() => setPulseRight(false), 300);
    }
  };

  return (
    <section className="pt-6 px-2 md:px-4 lg:px-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold text-[#000000DE]">
          {t('Featured Merchants')}
        </h2>

        <div className="hidden lg:flex gap-2">
          <button
            onClick={() => handleScroll('left')}
            className={`relative w-9 h-9 flex items-center justify-center rounded-md transition overflow-hidden
              ${swiperRef.current?.isBeginning ? 'bg-[#f5f5f5]' : 'bg-gray-200 hover:bg-[#ddd]'}`}
            disabled={swiperRef.current?.isBeginning}
          >
            {pulseLeft && <span className="absolute inset-0 rounded-md animate-pulse-button pointer-events-none bg-gray-400/60"></span>}
            <FaChevronRight className={`text-[20px] rotate-180 z-10 ${swiperRef.current?.isBeginning ? 'text-[#999]' : 'text-black'}`} />
          </button>

          <button
            onClick={() => handleScroll('right')}
            className={`relative w-9 h-9 flex items-center justify-center rounded-md transition overflow-hidden
              ${swiperRef.current?.isEnd ? 'bg-[#f5f5f5]' : 'bg-gray-200 hover:bg-[#ddd]'}`}
            disabled={swiperRef.current?.isEnd}
          >
            {pulseRight && <span className="absolute inset-0 rounded-md animate-pulse-button pointer-events-none bg-gray-400/60"></span>}
            <FaChevronRight className={`text-[20px] z-10 ${swiperRef.current?.isEnd ? 'text-[#999]' : 'text-black'}`} />
          </button>
        </div>
      </div>

      <div className="relative rounded-xl overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none lg:hidden absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-white to-transparent z-10"
        />
        <Swiper
          slidesPerView="auto"
          spaceBetween={14}
          watchOverflow
          modules={[Navigation]}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiperRef.current = swiper;
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onSlideChange={() => {
            // force rerender for button states
            swiperRef.current && setPulseLeft(p => p); 
          }}
        >
          {featuredMerchants.map((merchant, index) => (
            <SwiperSlide key={merchant._id || index} className="!w-auto !flex-shrink-0">
              <div
                className="cursor-pointer transition-all duration-300
                  w-[72px] sm:w-[84px] md:w-[96px]
                  lg:w-[108px] xl:w-[120px] 2xl:w-[140px]"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/merchant-details/${merchant._id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/merchant-details/${merchant._id}`); } }}
              >
                <div className="relative w-full pt-[100%] overflow-hidden rounded-xl">
                  <img
                    src={pickLogoImage(merchant)}
                    alt={merchant.storeName || 'merchant'}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <p className="mt-1.5 text-xs sm:text-sm font-semibold text-center truncate">
                  {merchant.storeName || ''}
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Hide default navigation arrows */}
        <style jsx>{`
          .swiper-button-prev, .swiper-button-next { display: none; }
        `}</style>
      </div>
    </section>
  );
};

export default FearuredMerchants;
